import React, { useMemo, useState } from "react";
import {
    Alert,
    Button,
    FileButton,
    Grid,
    Group,
    Switch,
    Text,
    TextInput,
} from "@mantine/core";
import { IconUpload, IconX } from "@tabler/icons-react";
import type { VisaApplication } from "../../types/VisaApplication";

export type VisaFormMode = "create" | "admin-edit";

type Props = {
    value: VisaApplication;
    onChange: (next: VisaApplication) => void;
    mode: VisaFormMode;
    file: File | null;
    setFile: (f: File | null) => void;
    showInvitation?: boolean;
    showSent?: boolean;
};

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
const ALLOWED_TYPES = ["image/png", "image/jpeg", "application/pdf"];

export default function VisaForm({
    value,
    onChange,
    mode,
    file,
    setFile,
    showInvitation = true,
    showSent,
}: Props) {
    const [fileError, setFileError] = useState<string | null>(null);

    const isAdmin = mode === "admin-edit";
    const canEditAdminFields = isAdmin;
    const shouldShowSent = typeof showSent === "boolean" ? showSent : isAdmin;
    const apiUrl = process.env.REACT_APP_API_URL ?? "";

    const set = <K extends keyof VisaApplication>(key: K, v: VisaApplication[K]) =>
        onChange({ ...value, [key]: v });

    const handleFileChange = (f: File | null) => {
        setFileError(null);

        if (!f) {
            setFile(null);
            return;
        }

        if (f.size > MAX_FILE_SIZE) {
            setFile(null);
            setFileError("Filen er for stor. Maks størrelse er 10 MB.");
            return;
        }

        if (!ALLOWED_TYPES.includes(f.type)) {
            setFile(null);
            setFileError("Kun PNG, JPG eller PDF filer er tilladt.");
            return;
        }

        setFile(f);
    };

    const clearSelectedFile = () => {
        setFile(null);
        setFileError(null);
    };

    const currentFileUrl = useMemo(() => {
        const raw = value.invitationLetterFile?.trim();
        if (!raw) return null;

        if (raw.startsWith("http://") || raw.startsWith("https://")) {
            return raw;
        }

        const normalizedFileName = raw.split("\\").pop()?.split("/").pop()?.trim();
        if (!normalizedFileName) return null;

        return `${apiUrl}/uploads/visa/${encodeURIComponent(normalizedFileName)}`;
    }, [apiUrl, value.invitationLetterFile]);

    return (
        <Grid gutter="md">
            <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput
                    label="First name"
                    value={value.firstName ?? ""}
                    onChange={(e) => set("firstName", e.currentTarget.value as any)}
                    required
                />
            </Grid.Col>

            <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput
                    label="Middle name"
                    value={value.middleName ?? ""}
                    onChange={(e) => set("middleName", (e.currentTarget.value || null) as any)}
                />
            </Grid.Col>

            <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput
                    label="Last name"
                    value={value.lastName ?? ""}
                    onChange={(e) => set("lastName", e.currentTarget.value as any)}
                    required
                />
            </Grid.Col>

            <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput
                    label="Nationality"
                    value={value.nationality ?? ""}
                    onChange={(e) => set("nationality", (e.currentTarget.value || null) as any)}
                />
            </Grid.Col>

            <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput
                    label="Date of birth"
                    type="date"
                    value={value.dateOfBirth?.slice(0, 10) ?? ""}
                    onChange={(e) =>
                        set("dateOfBirth", (e.currentTarget.value || null) as any)
                    }
                />
            </Grid.Col>

            <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput
                    label="Passport"
                    value={value.passportNo ?? ""}
                    onChange={(e) => set("passportNo", (e.currentTarget.value || null) as any)}
                />
            </Grid.Col>

            <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput
                    label="Expiry date"
                    type="date"
                    value={value.expiryDate?.slice(0, 10) ?? ""}
                    onChange={(e) =>
                        set("expiryDate", (e.currentTarget.value || null) as any)
                    }
                />
            </Grid.Col>

            <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput
                    label="Issue date"
                    type="date"
                    value={value.issueDate?.slice(0, 10) ?? ""}
                    onChange={(e) =>
                        set("issueDate", (e.currentTarget.value || null) as any)
                    }
                />
            </Grid.Col>

            <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput
                    label="Place of birth"
                    value={value.placeOfBirth ?? ""}
                    onChange={(e) => set("placeOfBirth", (e.currentTarget.value || null) as any)}
                />
            </Grid.Col>

            <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput
                    label="City to apply visa"
                    value={value.cityToApply ?? ""}
                    onChange={(e) => set("cityToApply", (e.currentTarget.value || null) as any)}
                />
            </Grid.Col>

            <Grid.Col span={12}>
                <TextInput
                    label="Email"
                    value={value.email ?? ""}
                    onChange={(e) => set("email" as any, (e.currentTarget.value || null) as any)}
                />
            </Grid.Col>

            {showInvitation && (
                <Grid.Col span={12}>
                    <Text size="sm" fw={500}>
                        Invitation letter
                    </Text>

                    {currentFileUrl ? (
                        <Group gap="sm" mt={4}>
                            <Button
                                component="a"
                                href={currentFileUrl}
                                target="_blank"
                                rel="noreferrer"
                                size="xs"
                                variant="subtle"
                            >
                                View current file
                            </Button>

                            {canEditAdminFields ? (
                                <FileButton
                                    onChange={handleFileChange}
                                    accept="image/png,image/jpeg,application/pdf"
                                >
                                    {(props) => (
                                        <Button
                                            {...props}
                                            size="xs"
                                            variant="light"
                                            leftSection={<IconUpload size={14} />}
                                        >
                                            Replace file
                                        </Button>
                                    )}
                                </FileButton>
                            ) : null}

                            {file ? (
                                <Button
                                    size="xs"
                                    variant="subtle"
                                    color="red"
                                    leftSection={<IconX size={14} />}
                                    onClick={clearSelectedFile}
                                >
                                    Remove selected
                                </Button>
                            ) : null}
                        </Group>
                    ) : canEditAdminFields ? (
                        <Group mt={4}>
                            <FileButton
                                onChange={handleFileChange}
                                accept="image/png,image/jpeg,application/pdf"
                            >
                                {(props) => (
                                    <Button
                                        {...props}
                                        size="xs"
                                        leftSection={<IconUpload size={14} />}
                                    >
                                        Upload file
                                    </Button>
                                )}
                            </FileButton>

                            {file ? (
                                <Button
                                    size="xs"
                                    variant="subtle"
                                    color="red"
                                    leftSection={<IconX size={14} />}
                                    onClick={clearSelectedFile}
                                >
                                    Remove selected
                                </Button>
                            ) : null}
                        </Group>
                    ) : (
                        <Text size="xs" c="dimmed" mt={4}>
                            No invitation letter uploaded yet.
                        </Text>
                    )}

                    {file && (
                        <Text size="xs" c="dimmed" mt={8}>
                            Selected file: {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                        </Text>
                    )}

                    {fileError && (
                        <Alert color="red" variant="light" title="Filen kunne ikke vælges" mt="sm">
                            {fileError}
                        </Alert>
                    )}

                    {!fileError && (
                        <Text size="xs" c="dimmed" mt={8}>
                            Tilladte filtyper: PNG, JPG og PDF. Maks størrelse: 10 MB.
                        </Text>
                    )}
                </Grid.Col>
            )}

            {shouldShowSent && (
                <Grid.Col span={12}>
                    <Switch
                        label="Sent"
                        checked={!!value.sent}
                        onChange={(e) => set("sent", e.currentTarget.checked as any)}
                        disabled={!canEditAdminFields}
                    />
                    {!canEditAdminFields && (
                        <Text size="xs" c="dimmed" mt={4}>
                            Status is managed by admins.
                        </Text>
                    )}
                </Grid.Col>
            )}
        </Grid>
    );
}